// Shared contract between web, api and worker. Everything that crosses a
// process boundary (HTTP body, queue payload, stored row shape) is declared
// here once, as a zod schema, and the TypeScript type is derived from it.
import { z } from "zod";
// ---- Clip: a cut of a YouTube video's audio ------------------------------
// Seconds, with sub-second precision. Walk-up clips are short by nature; the
// cap keeps a mis-tap from queuing a 40-minute download.
export const MAX_CLIP_SECONDS = 60;
export const YouTubeUrl = z
    .string()
    .trim()
    .url()
    .refine((u) => {
    try {
        const h = new URL(u).hostname.replace(/^www\./, "").replace(/^m\./, "");
        return h === "youtube.com" || h === "youtu.be" || h === "music.youtube.com";
    }
    catch {
        return false;
    }
}, "Must be a YouTube link");
export const ClipWindow = z
    .object({
    start: z.number().min(0),
    stop: z.number().min(0),
})
    .refine((w) => w.stop > w.start, { message: "Stop must be after start" })
    .refine((w) => w.stop - w.start <= MAX_CLIP_SECONDS, {
    message: `Clip must be ${MAX_CLIP_SECONDS}s or shorter`,
});
export const ClipRequest = z.object({
    url: YouTubeUrl,
    window: ClipWindow,
});
export const ClipStatus = z.enum(["queued", "extracting", "ready", "failed"]);
export const Clip = z.object({
    id: z.string().uuid(),
    ownerSub: z.string(),
    sourceUrl: z.string(),
    videoId: z.string(),
    title: z.string().nullable(),
    window: ClipWindow,
    status: ClipStatus,
    objectKey: z.string().nullable(), // bucket key once extracted
    durationSeconds: z.number().nullable(),
    error: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ---- Queue payload (api → worker) ----------------------------------------
export const EXTRACT_QUEUE = "mdj-extract";
export const ExtractJob = z.object({
    clipId: z.string().uuid(),
    url: YouTubeUrl,
    window: ClipWindow,
});
export const ExtractResult = z.object({
    objectKey: z.string(),
    durationSeconds: z.number(),
    title: z.string().nullable(),
});
// ---- Player ---------------------------------------------------------------
export const Player = z.object({
    id: z.string().uuid(),
    ownerSub: z.string(),
    name: z.string().trim().min(1).max(60),
    number: z.string().trim().max(3).nullable(),
    walkupClipId: z.string().uuid().nullable(),
    announcementKey: z.string().nullable(), // bucket key of the name announcement
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const PlayerCreate = Player.pick({ name: true, number: true });
export const PlayerAssign = z.object({
    walkupClipId: z.string().uuid().nullable(),
});
export const PlayerUpdate = Player.pick({ name: true, number: true }).partial();
// Full lineup order, first to last. Every id must belong to the roster.
export const PlayerOrder = z.object({ ids: z.array(z.string().uuid()).min(1) });
// ---- Helpers shared by web and worker ------------------------------------
export function youtubeVideoId(url) {
    try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, "").replace(/^m\./, "");
        if (host === "youtu.be")
            return u.pathname.slice(1).split("/")[0] || null;
        if (host.endsWith("youtube.com")) {
            const v = u.searchParams.get("v");
            if (v)
                return v;
            const m = u.pathname.match(/^\/(?:shorts|embed|live)\/([^/?]+)/);
            if (m)
                return m[1] ?? null;
        }
        return null;
    }
    catch {
        return null;
    }
}
export function formatClock(seconds) {
    const s = Math.max(0, seconds);
    const m = Math.floor(s / 60);
    const r = s - m * 60;
    return `${m}:${r.toFixed(1).padStart(4, "0")}`;
}
